/**
 * PKCE (Proof Key for Code Exchange) Utilities
 * 
 * Implements RFC 7636 for OAuth 2.0 authorization code flow security.
 * PKCE prevents authorization code interception attacks by using
 * cryptographic code verifiers and challenges.
 */

import { base64UrlEncode, sha256 } from './crypto.utils';

/**
 * Generate a cryptographically random code verifier
 * 
 * Per RFC 7636, the code verifier must be:
 * - Between 43 and 128 characters long
 * - Composed of unreserved characters: [A-Z] [a-z] [0-9] - . _ ~
 * 
 * @returns A random code verifier string
 */
export function generateCodeVerifier(): string {
  const length = 128; // Maximum length for best security
  const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
  const randomValues = new Uint8Array(length);
  const cryptoObj = globalThis.crypto || crypto;
  cryptoObj.getRandomValues(randomValues);
  
  return Array.from(randomValues)
    .map(value => charset[value % charset.length])
    .join('');
}

/**
 * Generate a code challenge from a code verifier
 * 
 * Per RFC 7636, the code challenge is:
 * - BASE64URL(SHA256(ASCII(code_verifier)))
 * 
 * @param verifier - The code verifier to hash
 * @returns A promise that resolves to the base64url-encoded SHA256 hash
 */
export async function generateCodeChallenge(verifier: string): Promise<string> {
  const hash = await sha256(verifier);
  return base64UrlEncode(hash);
}

/**
 * Verify that a code verifier matches a code challenge
 * 
 * @param verifier - The code verifier to check
 * @param challenge - The expected code challenge
 * @param method - The challenge method ('S256' or 'plain')
 * @returns True if the verifier matches the challenge
 */
export async function verifyCodeChallenge(
  verifier: string,
  challenge: string,
  method: 'S256' | 'plain' = 'S256'
): Promise<boolean> {
  if (method === 'plain') {
    return verifier === challenge;
  }
  
  // S256 method
  const computedChallenge = await generateCodeChallenge(verifier);
  return computedChallenge === challenge;
}
